import { useState, useRef, useEffect, useMemo } from 'react';
import { Skeleton } from '@solid/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  CheckSquare,
  Plus,
  ArrowUpDown,
  Filter,
  X,
  Edit2,
  ChevronDown,
  ChevronRight,
  MoreVertical,
  Eye,
  EyeOff,
  Archive,
  ArchiveRestore,
  Trash2,
  Check,
  ListChecks,
  ChevronLeft,
  Calendar,
  Flag,
  UserPlus,
  FolderInput,
} from 'lucide-react';
import { tasksApi, projectsApi } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { TaskDetailModal } from '@/components/modals/TaskDetailModal';
import { CreateTaskListModal } from '@/components/modals/CreateTaskListModal';
import { DuplicateTaskModal } from '@/components/modals/DuplicateTaskModal';
import { TaskItem } from '@/components/tasks/TaskItem';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { Avatar } from '@/components/ui/Avatar';
import { ViewToggle, TaskView } from '@/components/tasks/ViewToggle';
import { priorityOptions } from '@/components/tasks/pickers/PriorityPicker';
import { KanbanBoard } from '@/components/tasks/KanbanBoard';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { useTaskStatusConfigs } from '@/hooks/useTaskStatusConfigs';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { useProjectRoles } from '@/hooks/useProjectRoles';
import { CompletionCelebration } from '@/components/ui/CompletionCelebration';

type SortOption = 'default' | 'due-date-asc' | 'due-date-desc' | 'priority';

const priorityOrder = { URGENT: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };

function SortableRow({
  id,
  children,
}: {
  id: string;
  children: (dragHandle: React.ReactNode) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const handle = (
    <button
      type="button"
      {...attributes}
      {...listeners}
      onClick={(e) => e.stopPropagation()}
      className="cursor-grab active:cursor-grabbing p-0.5 mt-0.5 text-grey-300 hover:text-grey-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
      title="Drag to reorder"
      aria-label="Drag to reorder"
    >
      <GripVertical className="w-4 h-4" />
    </button>
  );
  return (
    <div ref={setNodeRef} style={style} className={cn(isDragging && 'opacity-50')}>
      {children(handle)}
    </div>
  );
}

export function TaskListDetail({
  listId: listIdProp,
  projectId: projectIdProp,
  initialFilterAssignee,
  onClose,
}: {
  /** When provided, overrides the route param (used by the takeover overlay). */
  listId?: string;
  projectId?: string;
  /** Pre-applies an assignee filter (carried over from the landing page). */
  initialFilterAssignee?: string | null;
  /** When provided, the view renders in overlay/takeover mode with a Close action. */
  onClose?: () => void;
} = {}) {
  const params = useParams<{ projectId: string; listId: string }>();
  const navigate = useNavigate();
  const projectId = projectIdProp ?? params.projectId;
  const listId = listIdProp ?? params.listId;
  // Rendered inside the full-screen takeover overlay (vs. as a route page).
  const overlay = !!onClose;
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  // Admin / staff of THIS project: Solid anywhere, a partner agency on its own.
  // Admin counts as staff (the app-role claim can make someone an admin even
  // when the org→AGENCY mapping is missing, as in local dev).
  const { isProjectAdmin: isAgencyAdmin, isProjectStaff } = useProjectRoles(projectId);
  const isAgencyMember = isAgencyAdmin || isProjectStaff;

  // Check if user is a client admin (admin of a non-agency organization)
  const isClientAdmin = !isAgencyMember && user?.organizations?.some(
    (org: any) => org.type !== 'AGENCY' && (org.role === 'client_admin' || org.role === 'Client Admin' || org.isOwner)
  );

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalStatusId, setCreateModalStatusId] = useState<string | undefined>(undefined);
  const [showEditListModal, setShowEditListModal] = useState(false);
  const [showListMenu, setShowListMenu] = useState(false);
  const [showDeleteListConfirm, setShowDeleteListConfirm] = useState(false);
  const listMenuRef = useRef<HTMLDivElement>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<any | null>(null);
  const [scrollToCommentId, setScrollToCommentId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('default');
  const [filterAssignees, setFilterAssignees] = useState<string[]>(initialFilterAssignee ? [initialFilterAssignee] : []);
  const toggleAssignee = (id: string) =>
    setFilterAssignees((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [deletedTaskId, setDeletedTaskId] = useState<string | null>(null);
  const [newTaskId, setNewTaskId] = useState<string | null>(null);
  const [duplicatingTask, setDuplicatingTask] = useState<any | null>(null);
  // Bulk selection
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);
  const [showBulkMenu, setShowBulkMenu] = useState(false);
  const [bulkView, setBulkView] = useState<'root' | 'status' | 'assignee' | 'priority' | 'list' | 'dueDate'>('root');
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const bulkMenuRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<TaskView>(() => {
    const stored = localStorage.getItem('solidhub-task-view');
    return stored === 'board' ? 'board' : 'list';
  });

  // Completed tasks display limits
  const COMPLETED_MAX_COUNT = 15;
  const COMPLETED_DAYS_WINDOW = 30;

  const sortMenuRef = useRef<HTMLDivElement>(null);
  const filterMenuRef = useRef<HTMLDivElement>(null);

  // Close menus when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (sortMenuRef.current && !sortMenuRef.current.contains(event.target as Node)) {
        setShowSortMenu(false);
      }
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target as Node)) {
        setShowFilterMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Persist view preference
  useEffect(() => {
    localStorage.setItem('solidhub-task-view', view);
  }, [view]);

  // Fetch task list details
  const { data: taskList, isLoading: listLoading } = useQuery({
    queryKey: ['taskList', listId],
    queryFn: () => tasksApi.getTaskList(projectId!, listId!),
    enabled: !!projectId && !!listId,
  });
  
  // Fetch tasks
  const { data: tasks, isLoading: tasksLoading } = useQuery({
    queryKey: ['tasks', listId],
    queryFn: () => tasksApi.listTasks(projectId!, listId!),
    enabled: !!projectId && !!listId,
  });

  // Fetch all task lists for the project (used for list switching in edit modal)
  const { data: allTaskLists } = useQuery({
    queryKey: ['taskLists', projectId],
    queryFn: () => tasksApi.listTaskLists(projectId!),
    enabled: !!projectId,
  });
  
  // Fetch a potentially deleted task when it's not in the active task list
  const { data: deletedTask } = useQuery({
    queryKey: ['task', deletedTaskId],
    queryFn: () => tasksApi.getTask(deletedTaskId!),
    enabled: !!deletedTaskId,
  });

  // Fetch project details to get members for assignment
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId!),
    enabled: !!projectId,
  });

  // Fetch task status configs
  const { data: statusConfigs } = useTaskStatusConfigs();

  const projectMembers = project?.members || [];
  // Clients can only create tasks when the project allows it; agency always can.
  const clientsCanCreate = (project?.settings as any)?.clientsCanCreateTasks !== false;
  const canCreateTask = isAgencyMember || clientsCanCreate;

  // Mutation for kanban drag reorder (status change and/or position within status).
  // No optimistic cache update — the KanbanBoard keeps local drag state for the
  // visual ordering, and the server response (via invalidate) brings canonical
  // positions back.
  const reorderTaskMutation = useMutation({
    mutationFn: ({ taskId, statusId, position }: { taskId: string; statusId: string; position: number }) =>
      tasksApi.reorderTask(taskId, { statusId, position }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', listId] });
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
    },
  });

  // List-level actions (mirrors the ⋮ menu on the tasks landing).
  const leaveList = () => {
    queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
    if (onClose) onClose();
    else navigate(`/projects/${projectId}/tasks`);
  };
  const archiveListMutation = useMutation({
    mutationFn: () => tasksApi.archiveTaskList(projectId!, listId!),
    onSuccess: leaveList,
  });
  const unarchiveListMutation = useMutation({
    mutationFn: () => tasksApi.unarchiveTaskList(projectId!, listId!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tasks', listId] }),
  });
  const deleteListMutation = useMutation({
    mutationFn: () => tasksApi.deleteTaskList(projectId!, listId!),
    onSuccess: () => {
      setShowDeleteListConfirm(false);
      leaveList();
    },
  });

  // Close the list menu when clicking outside.
  useEffect(() => {
    if (!showListMenu) return;
    const onClick = (e: MouseEvent) => {
      if (listMenuRef.current && !listMenuRef.current.contains(e.target as Node)) setShowListMenu(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showListMenu]);

  // Close the bulk-actions menu when clicking outside.
  useEffect(() => {
    if (!showBulkMenu) return;
    const onClick = (e: MouseEvent) => {
      if (bulkMenuRef.current && !bulkMenuRef.current.contains(e.target as Node)) {
        setShowBulkMenu(false);
        setBulkView('root');
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showBulkMenu]);

  // Leaving selection mode clears any pending selection + menu.
  useEffect(() => {
    if (!selectionMode) {
      setSelectedIds(new Set());
      setLastSelectedId(null);
      setShowBulkMenu(false);
      setBulkView('root');
    }
  }, [selectionMode]);

  // Open task from URL query param (for shared links)
  useEffect(() => {
    const taskId = searchParams.get('taskId');
    const commentId = searchParams.get('commentId');
    if (taskId && tasks && !selectedTask) {
      const task = tasks.find((t: any) => t.id === taskId);
      if (task) {
        setSelectedTask(task);
        setScrollToCommentId(commentId);
        // Auto-expand completed section if the task is completed
        if (task.status === 'COMPLETED') {
          setShowCompleted(true);
        }
        // Clear the query params from URL after opening
        searchParams.delete('taskId');
        searchParams.delete('commentId');
        setSearchParams(searchParams, { replace: true });
      } else if (!tasksLoading) {
        // Task not in list — may be deleted. Fetch individually.
        setDeletedTaskId(taskId);
        searchParams.delete('taskId');
        searchParams.delete('commentId');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, tasks, selectedTask, setSearchParams, tasksLoading]);
  
  // Track tasks that are fading out after completion
  const [completingTaskIds, setCompletingTaskIds] = useState<Set<string>>(new Set());

  // Toggle task completion
  const toggleTaskMutation = useMutation({
    mutationFn: (taskId: string) => tasksApi.toggleComplete(taskId),
    onSuccess: (_, taskId) => {
      const task = taskItems.find((t: any) => t.id === taskId);
      const wasCompleted = task?.status === 'COMPLETED';

      if (wasCompleted) {
        // Un-completing: just refresh immediately
        queryClient.invalidateQueries({ queryKey: ['tasks', listId] });
        queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
      } else {
        // Completing: play canvas animation (onComplete callback handles refresh)
        setCompletingTaskIds(prev => new Set(prev).add(taskId));
      }
    },
  });

  // Mutation for status changes from the list view (single-task status swap
  // without reorder). Reuses tasksApi.updateTask which accepts statusId.
  const updateStatusIdMutation = useMutation({
    mutationFn: ({ taskId, statusId }: { taskId: string; statusId: string }) =>
      tasksApi.updateTask(taskId, { statusId }),
    onMutate: async ({ taskId, statusId }) => {
      await queryClient.cancelQueries({ queryKey: ['tasks', listId] });
      const previousTasks = queryClient.getQueryData<any[]>(['tasks', listId]);
      const targetStatus = statusConfigs?.find((s) => s.id === statusId);
      queryClient.setQueryData<any[]>(['tasks', listId], (old) => {
        if (!old) return old;
        return old.map((t) =>
          t.id === taskId
            ? {
                ...t,
                statusId,
                statusConfig: targetStatus ?? t.statusConfig,
                status: targetStatus?.isClosed ? 'COMPLETED' : t.status,
                completedAt: targetStatus?.isClosed
                  ? t.completedAt ?? new Date().toISOString()
                  : t.statusConfig?.isClosed
                    ? null
                    : t.completedAt,
              }
            : t,
        );
      });
      return { previousTasks };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousTasks) {
        queryClient.setQueryData(['tasks', listId], context.previousTasks);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', listId] });
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
    },
  });

  // Mutation for list-view drag reorder (same list, new position).
  const moveTaskPositionMutation = useMutation({
    mutationFn: ({ taskId, position }: { taskId: string; position: number }) =>
      tasksApi.moveTask(taskId, listId!, position),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', listId] });
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
    },
  });

  // Mutation for updating task priority (from kanban priority picker)
  const updatePriorityMutation = useMutation({
    mutationFn: ({ taskId, priority }: { taskId: string; priority: string }) =>
      tasksApi.updateTask(taskId, { priority }),
    onMutate: async ({ taskId, priority }) => {
      await queryClient.cancelQueries({ queryKey: ['tasks', listId] });
      const previousTasks = queryClient.getQueryData<any[]>(['tasks', listId]);
      queryClient.setQueryData<any[]>(['tasks', listId], (old) => {
        if (!old) return old;
        return old.map((t) => (t.id === taskId ? { ...t, priority } : t));
      });
      return { previousTasks };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousTasks) {
        queryClient.setQueryData(['tasks', listId], context.previousTasks);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', listId] });
    },
  });

  // Mutation for updating task due date (from kanban date picker)
  const updateStartDateMutation = useMutation({
    mutationFn: ({ taskId, startDate }: { taskId: string; startDate: string | null }) =>
      tasksApi.updateTask(taskId, { startDate }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tasks', listId] }),
  });

  const updateDueDateMutation = useMutation({
    mutationFn: ({ taskId, dueDate }: { taskId: string; dueDate: string | null }) =>
      tasksApi.updateTask(taskId, { dueDate }),
    onMutate: async ({ taskId, dueDate }) => {
      await queryClient.cancelQueries({ queryKey: ['tasks', listId] });
      const previousTasks = queryClient.getQueryData<any[]>(['tasks', listId]);
      queryClient.setQueryData<any[]>(['tasks', listId], (old) => {
        if (!old) return old;
        return old.map((t) => (t.id === taskId ? { ...t, dueDate } : t));
      });
      return { previousTasks };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousTasks) {
        queryClient.setQueryData(['tasks', listId], context.previousTasks);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', listId] });
    },
  });

  // Mutation for updating multiple co-assignees (all equal)
  const updateAssigneesMutation = useMutation({
    mutationFn: ({ taskId, assigneeIds }: { taskId: string; assigneeIds: string[] }) =>
      tasksApi.updateTask(taskId, { assigneeIds }),
    onMutate: async ({ taskId, assigneeIds }) => {
      await queryClient.cancelQueries({ queryKey: ['tasks', listId] });
      const previousTasks = queryClient.getQueryData<any[]>(['tasks', listId]);
      const people = projectMembers
        .filter((m: any) => assigneeIds.includes(m.user.id))
        .map((m: any) => m.user);
      queryClient.setQueryData<any[]>(['tasks', listId], (old) => {
        if (!old) return old;
        return old.map((t) =>
          t.id === taskId
            ? {
                ...t,
                assignees: people,
                assigneeId: assigneeIds[0] ?? null,
                assignee: people[0] ?? null,
              }
            : t,
        );
      });
      return { previousTasks };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousTasks) {
        queryClient.setQueryData(['tasks', listId], context.previousTasks);
      }
    },
    onSettled: (_d, _e, vars) => {
      queryClient.invalidateQueries({ queryKey: ['tasks', listId] });
      if (vars?.taskId) queryClient.invalidateQueries({ queryKey: ['task', vars.taskId] });
    },
  });

  // Update task status (for In Progress toggle)
  const updateStatusMutation = useMutation({
    mutationFn: ({ taskId, status }: { taskId: string; status: string }) =>
      tasksApi.updateTask(taskId, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', listId] });
    },
  });

  // Delete task mutation
  const deleteTaskMutation = useMutation({
    mutationFn: (id: string) => tasksApi.deleteTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', listId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      setDeleteConfirmId(null);
    },
  });
  
  // ---- Bulk actions ----
  // Optimistically patch every selected task, then reconcile with the server.
  const bulkUpdateMutation = useMutation({
    mutationFn: ({ ids, data }: { ids: string[]; data: any }) =>
      tasksApi.bulkUpdateTasks(ids, data),
    onMutate: async ({ ids, data }) => {
      await queryClient.cancelQueries({ queryKey: ['tasks', listId] });
      const previousTasks = queryClient.getQueryData<any[]>(['tasks', listId]);
      const idSet = new Set(ids);
      const targetStatus = data.statusId ? statusConfigs?.find((s) => s.id === data.statusId) : undefined;
      const member = 'assigneeId' in data
        ? projectMembers.find((m: any) => m.user.id === data.assigneeId)
        : undefined;
      queryClient.setQueryData<any[]>(['tasks', listId], (old) => {
        if (!old) return old;
        // A move to another list removes the tasks from this list entirely.
        if (data.listId) return old.filter((t) => !idSet.has(t.id));
        return old.map((t) => {
          if (!idSet.has(t.id)) return t;
          const next: any = { ...t };
          if (data.statusId) {
            next.statusId = data.statusId;
            next.statusConfig = targetStatus ?? t.statusConfig;
            if (targetStatus?.isClosed) {
              next.status = 'COMPLETED';
              next.completedAt = t.completedAt ?? new Date().toISOString();
            } else if (t.statusConfig?.isClosed) {
              next.status = 'PENDING';
              next.completedAt = null;
            }
          }
          if ('assigneeId' in data) {
            next.assigneeId = data.assigneeId ?? null;
            next.assignee = data.assigneeId && member ? member.user : null;
          }
          if ('dueDate' in data) next.dueDate = data.dueDate ?? null;
          if (data.priority) next.priority = data.priority;
          return next;
        });
      });
      return { previousTasks };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousTasks) queryClient.setQueryData(['tasks', listId], context.previousTasks);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', listId] });
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => tasksApi.bulkDeleteTasks(ids),
    onMutate: async (ids) => {
      await queryClient.cancelQueries({ queryKey: ['tasks', listId] });
      const previousTasks = queryClient.getQueryData<any[]>(['tasks', listId]);
      const idSet = new Set(ids);
      queryClient.setQueryData<any[]>(['tasks', listId], (old) => old?.filter((t) => !idSet.has(t.id)));
      return { previousTasks };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousTasks) queryClient.setQueryData(['tasks', listId], context.previousTasks);
    },
    onSuccess: () => {
      setSelectedIds(new Set());
      setSelectionMode(false);
      setShowBulkDeleteConfirm(false);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', listId] });
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });

  const applyBulk = (data: any) => {
    if (selectedIds.size === 0) return;
    bulkUpdateMutation.mutate({ ids: [...selectedIds], data });
    setShowBulkMenu(false);
    setBulkView('root');
  };

  const isLoading = listLoading || tasksLoading;
  const taskItems = tasks || [];

  // Get unique assignees from tasks for filter dropdown
  const assignees = taskItems.reduce((acc: any[], task: any) => {
    if (task.assignee && !acc.find((a) => a.id === task.assignee.id)) {
      acc.push(task.assignee);
    }
    return acc;
  }, []);

  // Apply filtering. Memoized so the reference is stable across renders —
  // otherwise KanbanBoard's re-sync effect fires every render and clobbers an
  // in-flight optimistic drag/drop with stale server data (snap-back).
  const filteredTasks = useMemo(
    () =>
      taskItems.filter((t: any) => {
        if (filterAssignees.length === 0) return true;
        const ids = (t.assignees?.length ? t.assignees.map((a: any) => a.id) : [t.assigneeId]).filter(
          Boolean,
        );
        return ids.some((id: string) => filterAssignees.includes(id));
      }),
    [taskItems, filterAssignees],
  );

  // Board props are memoized so their references stay stable across renders —
  // otherwise KanbanBoard's re-sync effect fires every render and clobbers an
  // in-flight optimistic drop (snap-back). "Show Completed" toggles inclusion.
  const boardTasks = useMemo(
    () => (showCompleted ? filteredTasks : filteredTasks.filter((t: any) => t.status !== 'COMPLETED')),
    [filteredTasks, showCompleted],
  );
  const boardStatusConfigs = useMemo(
    () => (showCompleted ? (statusConfigs ?? []) : (statusConfigs ?? []).filter((s: any) => !s.isClosed)),
    [statusConfigs, showCompleted],
  );

  // Sorting function
  const sortTasks = (tasks: any[]) => {
    if (sortBy === 'default') return tasks;

    return [...tasks].sort((a, b) => {
      switch (sortBy) {
        case 'due-date-asc':
          if (!a.dueDate && !b.dueDate) return 0;
          if (!a.dueDate) return 1;
          if (!b.dueDate) return -1;
          return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
        case 'due-date-desc':
          if (!a.dueDate && !b.dueDate) return 0;
          if (!a.dueDate) return 1;
          if (!b.dueDate) return -1;
          return new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime();
        case 'priority': {
          const aPriority = priorityOrder[a.priority as keyof typeof priorityOrder] ?? 2;
          const bPriority = priorityOrder[b.priority as keyof typeof priorityOrder] ?? 2;
          return aPriority - bPriority;
        }
        default:
          return 0;
      }
    });
  };

  // Separate completed and incomplete tasks, then sort
  const incompleteTasks = sortTasks(filteredTasks.filter((t: any) => t.status !== 'COMPLETED'));

  // Filter completed tasks: last 30 days, max 15
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - COMPLETED_DAYS_WINDOW);

  const allCompletedTasks = sortTasks(filteredTasks.filter((t: any) => t.status === 'COMPLETED'));
  const recentCompletedTasks = allCompletedTasks.filter((t: any) => {
    const completedDate = t.completedAt ? new Date(t.completedAt) : new Date(t.updatedAt);
    return completedDate >= thirtyDaysAgo;
  });
  const completedTasks = recentCompletedTasks.slice(0, COMPLETED_MAX_COUNT);
  const hiddenCompletedCount = allCompletedTasks.length - completedTasks.length;

  // ---- Selection helpers ----
  // Flat display order of currently-visible rows, used for shift-range select.
  // Non-editable tasks are simply skipped server-side by the bulk endpoint.
  const orderedVisibleIds = [...incompleteTasks, ...completedTasks].map((t: any) => t.id);
  const selectableCount = orderedVisibleIds.length;
  const allVisibleSelected = selectableCount > 0 && orderedVisibleIds.every((id) => selectedIds.has(id));

  const toggleSelect = (id: string, shiftKey: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (shiftKey && lastSelectedId && lastSelectedId !== id) {
        const a = orderedVisibleIds.indexOf(lastSelectedId);
        const b = orderedVisibleIds.indexOf(id);
        if (a !== -1 && b !== -1) {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          const shouldSelect = !prev.has(id);
          for (let i = lo; i <= hi; i++) {
            if (shouldSelect) next.add(orderedVisibleIds[i]);
            else next.delete(orderedVisibleIds[i]);
          }
          return next;
        }
      }
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setLastSelectedId(id);
  };
  const selectAllVisible = () => setSelectedIds(new Set(orderedVisibleIds));
  const clearSelection = () => setSelectedIds(new Set());
  // Board selection: toggle directly. Non-editable tasks are skipped server-side
  // by the bulk endpoint, so we don't silently swallow the click here.
  const selectFromBoard = (id: string, shiftKey: boolean) => toggleSelect(id, shiftKey);

  const otherLists = (allTaskLists ?? []).filter((l: any) => l.id !== listId);

  const selectedAssignees = assignees.filter((a) => filterAssignees.includes(a.id));
  const sortLabels: Record<SortOption, string> = {
    'default': 'Default',
    'due-date-asc': 'Due Date (Earliest)',
    'due-date-desc': 'Due Date (Latest)',
    'priority': 'Priority (Highest)',
  };

  // Hooks MUST be called before any early return to keep the hook order stable.
  const listSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );

  if (isLoading) {
    return (
      <div aria-busy>
        <Skeleton className="mb-6 h-5 w-40" />
        <Card>
          <Skeleton className="mb-2 h-8 w-48" />
          <Skeleton className="mb-6 h-4 w-32" />
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
        </Card>
      </div>
    );
  }
  
  const renderTask = (task: any, opts: { sortable?: boolean } = {}) => {
    const isInProgress = task.status === 'IN_PROGRESS';
    const isAssignee = task.assigneeId === user?.id;
    const isCompleting = completingTaskIds.has(task.id);

    // Permission: can edit task
    // - Agency members: can edit any task
    // - Clients: can only edit tasks they created
    const canEditTask = isAgencyMember || task.createdById === user?.id;

    // Permission: can toggle completion
    // - Agency members: can toggle any task
    // - Clients: can only toggle tasks assigned to them
    const canToggle = isAgencyMember || isAssignee;

    const taskItemNode = (dragHandleNode?: React.ReactNode) => (
      <TaskItem
        task={task}
        currentUserId={user?.id}
        onClick={() => setSelectedTask(task)}
        onToggleComplete={canToggle ? () => toggleTaskMutation.mutate(task.id) : undefined}
        onToggleInProgress={() => updateStatusMutation.mutate({
          taskId: task.id,
          status: isInProgress ? 'PENDING' : 'IN_PROGRESS',
        })}
        onEdit={canEditTask ? () => setSelectedTask(task) : undefined}
        onDelete={canEditTask ? () => setDeleteConfirmId(task.id) : undefined}
        onDuplicate={() => setDuplicatingTask(task)}
        isToggling={toggleTaskMutation.isPending}
        isUpdatingStatus={updateStatusMutation.isPending}
        columns
        isNew={task.id === newTaskId}
        onNewHighlightEnd={() => setNewTaskId(null)}
        projectMembers={isAgencyMember ? projectMembers : undefined}
        statusConfigs={isAgencyMember ? statusConfigs : undefined}
        onUpdateAssignees={isAgencyMember ? (assigneeIds) => updateAssigneesMutation.mutate({ taskId: task.id, assigneeIds }) : undefined}
        onUpdateStartDate={isAgencyMember ? (startDate) => updateStartDateMutation.mutate({ taskId: task.id, startDate }) : undefined}
        onUpdateDueDate={isAgencyMember ? (dueDate) => updateDueDateMutation.mutate({ taskId: task.id, dueDate }) : undefined}
        onUpdatePriority={isAgencyMember ? (priority) => updatePriorityMutation.mutate({ taskId: task.id, priority }) : undefined}
        onUpdateStatus={isAgencyMember ? (statusId) => updateStatusIdMutation.mutate({ taskId: task.id, statusId }) : undefined}
        dragHandle={dragHandleNode}
        selectionMode={selectionMode}
        selected={selectedIds.has(task.id)}
        onSelectChange={(_checked, shiftKey) => toggleSelect(task.id, shiftKey)}
      />
    );

    const inner = opts.sortable && canEditTask ? (
      <SortableRow id={task.id}>
        {(dragHandleNode) => taskItemNode(dragHandleNode)}
      </SortableRow>
    ) : (
      taskItemNode()
    );

    return (
      <div
        key={task.id}
        className="relative"
        style={isCompleting ? { overflow: 'visible' } : undefined}
      >
        <div className={cn(isCompleting && 'animate-poof-row pointer-events-none')}>
          {inner}
        </div>
        <CompletionCelebration
          show={isCompleting}
          onComplete={() => {
            setCompletingTaskIds(prev => {
              const next = new Set(prev);
              next.delete(task.id);
              return next;
            });
            queryClient.invalidateQueries({ queryKey: ['tasks', listId] });
            queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
          }}
        />
      </div>
    );
  };

  const canReorderList = sortBy === 'default' && filterAssignees.length === 0 && isAgencyMember;

  const handleListDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const activeIndex = incompleteTasks.findIndex((t: any) => t.id === active.id);
    const overIndex = incompleteTasks.findIndex((t: any) => t.id === over.id);
    if (activeIndex === -1 || overIndex === -1) return;
    const reordered = arrayMove(incompleteTasks, activeIndex, overIndex);
    // Optimistic cache update: rewrite positions to reflect new order.
    queryClient.setQueryData<any[]>(['tasks', listId], (old) => {
      if (!old) return old;
      const byId = new Map(reordered.map((t: any, idx: number) => [t.id, idx]));
      return [...old].sort((a, b) => {
        const ai = byId.has(a.id) ? byId.get(a.id)! : 1e9;
        const bi = byId.has(b.id) ? byId.get(b.id)! : 1e9;
        return ai - bi;
      });
    });
    moveTaskPositionMutation.mutate({
      taskId: active.id as string,
      position: overIndex,
    });
  };

  // Select toggle + (when items chosen) actions dropdown. Rendered inline in
  // the sort/filter bar, to the right of the Assignee filter.
  const bulkControls = taskItems.length > 0 ? (
    <div className="flex items-center gap-2" ref={bulkMenuRef}>
      <button
        onClick={() => setSelectionMode((v) => !v)}
        aria-pressed={selectionMode}
        title={selectionMode ? 'Exit selection mode' : 'Select tasks'}
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
          selectionMode ? 'bg-secondary text-white' : 'bg-grey-100 text-grey-600 hover:bg-grey-200',
        )}
      >
        <ListChecks className="w-4 h-4" />
        Select
      </button>

      {selectionMode && selectedIds.size > 0 && (
        <div className="relative">
          <button
            onClick={() => { setShowBulkMenu((v) => !v); setBulkView('root'); }}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-accent text-white hover:bg-accent/90 transition-colors"
          >
            {selectedIds.size} selected
            <ChevronDown className="w-4 h-4" />
          </button>

          {showBulkMenu && (
            <div className="absolute left-0 top-full mt-1 w-60 bg-white rounded-lg shadow-elevated border border-grey-200 py-1 z-50 max-h-96 overflow-y-auto">
              {bulkView === 'root' && (
                <>
                  <button onClick={() => setBulkView('status')} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50">
                    <span className="flex items-center gap-2"><Check className="w-4 h-4" /> Set status</span>
                    <ChevronRight className="w-4 h-4 text-grey-400" />
                  </button>
                  <button onClick={() => setBulkView('assignee')} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50">
                    <span className="flex items-center gap-2"><UserPlus className="w-4 h-4" /> Assign</span>
                    <ChevronRight className="w-4 h-4 text-grey-400" />
                  </button>
                  <button onClick={() => setBulkView('dueDate')} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50">
                    <span className="flex items-center gap-2"><Calendar className="w-4 h-4" /> Set due date</span>
                    <ChevronRight className="w-4 h-4 text-grey-400" />
                  </button>
                  <button onClick={() => setBulkView('priority')} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50">
                    <span className="flex items-center gap-2"><Flag className="w-4 h-4" /> Set priority</span>
                    <ChevronRight className="w-4 h-4 text-grey-400" />
                  </button>
                  {otherLists.length > 0 && (
                    <button onClick={() => setBulkView('list')} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50">
                      <span className="flex items-center gap-2"><FolderInput className="w-4 h-4" /> Move to list</span>
                      <ChevronRight className="w-4 h-4 text-grey-400" />
                    </button>
                  )}
                  <div className="my-1 border-t border-grey-100" />
                  <button onClick={() => { setShowBulkMenu(false); setShowBulkDeleteConfirm(true); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-error hover:bg-error/5">
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </>
              )}

              {bulkView !== 'root' && (
                <button onClick={() => setBulkView('root')} className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-grey-500 hover:bg-grey-50 border-b border-grey-100 mb-1">
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
              )}

              {bulkView === 'status' && (statusConfigs ?? []).map((s) => (
                <button key={s.id} onClick={() => applyBulk({ statusId: s.id })} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50">
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                  {s.name}
                </button>
              ))}

              {bulkView === 'assignee' && (
                <>
                  <button onClick={() => applyBulk({ assigneeId: null })} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50">
                    <span className="w-5 h-5 rounded-full bg-grey-100 flex-shrink-0" /> Unassigned
                  </button>
                  {projectMembers.map((m: any) => (
                    <button key={m.user.id} onClick={() => applyBulk({ assigneeId: m.user.id })} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50">
                      <Avatar src={m.user.avatarUrl} name={m.user.name} size="xs" />
                      {m.user.name}
                    </button>
                  ))}
                </>
              )}

              {bulkView === 'priority' && priorityOptions.map((p) => (
                <button key={p.value} onClick={() => applyBulk({ priority: p.value })} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50">
                  <Flag className="w-4 h-4" style={{ color: p.color, fill: p.color }} /> {p.label}
                </button>
              ))}

              {bulkView === 'list' && otherLists.map((l: any) => (
                <button key={l.id} onClick={() => applyBulk({ listId: l.id })} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50">
                  <FolderInput className="w-4 h-4 text-grey-400" /> {l.name}
                </button>
              ))}

              {bulkView === 'dueDate' && (
                <div className="px-3 py-2 space-y-2">
                  <input
                    type="date"
                    onChange={(e) => e.target.value && applyBulk({ dueDate: e.target.value })}
                    className="w-full rounded-lg border border-grey-200 px-2 py-1.5 text-sm text-secondary focus:border-accent focus:outline-none"
                  />
                  <button onClick={() => applyBulk({ dueDate: null })} className="w-full text-left px-1 py-1 text-sm text-grey-500 hover:text-secondary">
                    Clear due date
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className={overlay ? 'h-full flex flex-col min-h-0' : 'w-full'}>
      {/* Task Detail Modal */}
      {selectedTask && project && (
        <TaskDetailModal
          isOpen={!!selectedTask}
          onClose={() => setSelectedTask(null)}
          onDuplicate={() => {
            setDuplicatingTask(selectedTask);
            setSelectedTask(null);
          }}
          task={selectedTask}
          projectId={projectId!}
          listId={listId!}
          organizationId={project.organizationId}
          scrollToCommentId={scrollToCommentId ?? undefined}
        />
      )}

      {/* Deleted task modal */}
      {deletedTask?.deletedAt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-secondary/60 backdrop-blur-sm"
            onClick={() => setDeletedTaskId(null)}
          />
          <div className="relative bg-white rounded-xl shadow-elevated p-6 max-w-sm w-full">
            <h3 className="text-heading-4 text-secondary mb-2">{deletedTask.title}</h3>
            <p className="text-grey-500 text-sm mb-6">This task was deleted.</p>
            <div className="flex justify-end">
              <Button variant="ghost" onClick={() => setDeletedTaskId(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-secondary/60 backdrop-blur-sm" onClick={() => setDeleteConfirmId(null)} />
          <div className="relative bg-white rounded-xl shadow-elevated p-6 max-w-sm w-full">
            <h3 className="text-heading-4 text-secondary mb-2">Delete Task?</h3>
            <p className="text-grey-500 text-sm mb-6">
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setDeleteConfirmId(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => deleteTaskMutation.mutate(deleteConfirmId)}
                isLoading={deleteTaskMutation.isPending}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Back to list index (route mode only — the takeover has its own close button) */}
      {!overlay && (
        <Link
          to={`/projects/${projectId}/tasks`}
          className="inline-flex items-center gap-2 text-sm text-grey-500 hover:text-secondary mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Task Lists
        </Link>
      )}

      <Card className={overlay ? 'flex-1 flex flex-col min-h-0 rounded-2xl shadow-2xl' : 'mb-6'}>
        {/* Header */}
        <div className="flex items-start justify-between mb-6 flex-shrink-0">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-secondary">{taskList?.name}</h1>
              {taskList?.archivedAt && (
                <span className="inline-flex items-center gap-1 text-xs font-normal text-grey-400">
                  <Archive className="w-3 h-3" />
                  Archived
                </span>
              )}
            </div>
            <p className="text-sm text-grey-500 mt-1 flex items-center gap-2 flex-wrap">
              <span>{incompleteTasks.length} left - {completedTasks.length} done</span>
              {selectedAssignees.map((a) => (
                <span key={a.id} className="inline-flex items-center gap-1 rounded-full bg-secondary/10 pl-2 pr-1 py-0.5 text-xs font-medium text-secondary">
                  <Avatar src={a.avatarUrl} name={a.name} size="xs" />
                  {a.name}
                  <button
                    onClick={() => toggleAssignee(a.id)}
                    className="rounded-full p-0.5 hover:bg-secondary/20"
                    title="Remove assignee filter"
                    aria-label="Remove assignee filter"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {statusConfigs && statusConfigs.length > 0 && (
              <ViewToggle view={view} onViewChange={setView} />
            )}

            {canCreateTask && (
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="w-4 h-4" />
                Add Task
              </Button>
            )}
            {/* List options menu (mirrors the tasks landing card menu) */}
            <div className="relative" ref={listMenuRef}>
              <button
                onClick={() => setShowListMenu(!showListMenu)}
                className="p-2 rounded-lg hover:bg-grey-100 text-grey-400 hover:text-accent transition-colors"
                title="List options"
                aria-label="List options"
              >
                <MoreVertical className="w-5 h-5" />
              </button>
              {showListMenu && (
                <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-lg shadow-elevated border border-grey-200 py-1 z-50">
                  {canCreateTask && (
                    <button onClick={() => { setShowCreateModal(true); setShowListMenu(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50">
                      <Plus className="w-4 h-4" /> Add a task
                    </button>
                  )}
                  {completedTasks.length > 0 && (
                    <button onClick={() => { setShowCompleted(!showCompleted); setShowListMenu(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50">
                      {showCompleted ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      {showCompleted ? 'Hide Completed Tasks' : 'Show Completed Tasks'}
                    </button>
                  )}
                  {(isAgencyMember || taskList?.createdById === user?.id) && (
                    <button onClick={() => { setShowEditListModal(true); setShowListMenu(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50">
                      <Edit2 className="w-4 h-4" /> Edit
                    </button>
                  )}
                  {isAgencyMember && !taskList?.archivedAt && (
                    <button onClick={() => { archiveListMutation.mutate(); setShowListMenu(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50">
                      <Archive className="w-4 h-4" /> Archive
                    </button>
                  )}
                  {isAgencyMember && taskList?.archivedAt && (
                    <button onClick={() => { unarchiveListMutation.mutate(); setShowListMenu(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-secondary hover:bg-grey-50">
                      <ArchiveRestore className="w-4 h-4" /> Unarchive
                    </button>
                  )}
                  {isAgencyMember && (
                    <button onClick={() => { setShowDeleteListConfirm(true); setShowListMenu(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-error hover:bg-error/5">
                      <Trash2 className="w-4 h-4" /> Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

      {/* Selection bar (bulk mode) */}
      {selectionMode && (
        <div className={cn('flex items-center gap-3 mb-4 text-sm flex-shrink-0', overlay && 'flex-shrink-0')}>
          <span className="font-medium text-secondary">{selectedIds.size} selected</span>
          <button
            onClick={allVisibleSelected ? clearSelection : selectAllVisible}
            className="text-accent hover:underline"
          >
            {allVisibleSelected ? 'Clear' : `Select all (${selectableCount})`}
          </button>
          {selectedIds.size > 0 && !allVisibleSelected && (
            <button onClick={clearSelection} className="text-grey-500 hover:text-secondary">
              Clear
            </button>
          )}
          <span className="text-grey-400">· Tip: shift-click to select a range</span>
        </div>
      )}

      {/* Sort & Filter Controls (shared across board + list views) */}
      {taskItems.length > 0 && (
        <div className={cn('flex items-center gap-2 mb-6 flex-wrap', overlay && 'flex-shrink-0')}>
          {/* Sort Dropdown */}
          <div className="relative" ref={sortMenuRef}>
            <button
              onClick={() => { setShowSortMenu(!showSortMenu); setShowFilterMenu(false); }}
              className={cn(
                'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                sortBy !== 'default'
                  ? 'bg-secondary text-white'
                  : 'bg-grey-100 text-grey-600 hover:bg-grey-200'
              )}
            >
              <ArrowUpDown className="w-4 h-4" />
              {sortBy !== 'default' ? sortLabels[sortBy] : 'Sort'}
            </button>
            {showSortMenu && (
              <div className="absolute left-0 top-full mt-1 w-48 bg-white rounded-lg shadow-elevated border border-grey-200 py-1 z-50">
                {(Object.keys(sortLabels) as SortOption[]).map((option) => (
                  <button
                    key={option}
                    onClick={() => { setSortBy(option); setShowSortMenu(false); }}
                    className={cn(
                      'w-full text-left px-3 py-2 text-sm hover:bg-grey-50 transition-colors',
                      sortBy === option ? 'text-primary font-medium' : 'text-secondary'
                    )}
                  >
                    {sortLabels[option]}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Assignee Filter Dropdown */}
          {assignees.length > 0 && (
            <div className="relative" ref={filterMenuRef}>
              <button
                onClick={() => { setShowFilterMenu(!showFilterMenu); setShowSortMenu(false); }}
                className={cn(
                  'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                  filterAssignees.length > 0
                    ? 'bg-secondary text-white'
                    : 'bg-grey-100 text-grey-600 hover:bg-grey-200'
                )}
              >
                <Filter className="w-4 h-4" />
                {filterAssignees.length === 0
                  ? 'Assignee'
                  : filterAssignees.length === 1
                    ? selectedAssignees[0]?.name
                    : `${filterAssignees.length} assignees`}
              </button>
              {showFilterMenu && (
                <div className="absolute left-0 top-full mt-1 w-56 max-h-72 overflow-y-auto bg-white rounded-lg shadow-elevated border border-grey-200 py-1 z-50">
                  <button
                    onClick={() => setFilterAssignees([])}
                    className={cn(
                      'w-full text-left px-3 py-2 text-sm hover:bg-grey-50 transition-colors',
                      filterAssignees.length === 0 ? 'text-primary font-medium' : 'text-secondary'
                    )}
                  >
                    All Assignees
                  </button>
                  {assignees.map((assignee) => {
                    const checked = filterAssignees.includes(assignee.id);
                    return (
                      <button
                        key={assignee.id}
                        onClick={() => toggleAssignee(assignee.id)}
                        className={cn(
                          'w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-grey-50 transition-colors',
                          checked ? 'text-primary font-medium' : 'text-secondary'
                        )}
                      >
                        <span className={cn(
                          'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border',
                          checked ? 'bg-accent border-accent text-white' : 'border-grey-300'
                        )}>
                          {checked && <Check className="w-3 h-3" />}
                        </span>
                        <Avatar src={assignee.avatarUrl} name={assignee.name} size="xs" />
                        {assignee.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Bulk-select toggle + actions dropdown (to the right of Assignee) */}
          {bulkControls}

          {/* Clear Filters */}
          {(sortBy !== 'default' || filterAssignees.length > 0) && (
            <button
              onClick={() => { setSortBy('default'); setFilterAssignees([]); }}
              className="flex items-center gap-1 px-2 py-1.5 text-sm text-grey-500 hover:text-secondary transition-colors"
            >
              <X className="w-3 h-3" />
              Clear
            </button>
          )}
        </div>
      )}

      {/* Board View */}
      {view === 'board' && statusConfigs && statusConfigs.length > 0 && (
        <div
          className={overlay
            ? 'flex-1 min-h-0 -mx-6 px-6 overflow-hidden'
            : 'mb-6 -mx-6 -mb-6 px-6 pb-6 overflow-x-auto'}
        >
          <ErrorBoundary>
            <KanbanBoard
              tasks={boardTasks}
              statusConfigs={boardStatusConfigs}
              boardKey={listId}
              fillHeight={overlay}
              projectMembers={projectMembers}
              onTaskClick={(task) => setSelectedTask(task)}
              onTaskReorder={isAgencyMember ? (taskId, statusId, position) => {
                reorderTaskMutation.mutate({ taskId, statusId, position });
              } : undefined}
              onTaskAssigneesChange={isAgencyMember ? (taskId, assigneeIds) => {
                updateAssigneesMutation.mutate({ taskId, assigneeIds });
              } : undefined}
              onTaskDueDateChange={isAgencyMember ? (taskId, dueDate) => {
                updateDueDateMutation.mutate({ taskId, dueDate });
              } : undefined}
              onTaskPriorityChange={isAgencyMember ? (taskId, priority) => {
                updatePriorityMutation.mutate({ taskId, priority });
              } : undefined}
              onAddTask={canCreateTask ? (statusId) => {
                setCreateModalStatusId(statusId);
                setShowCreateModal(true);
              } : undefined}
              selectionMode={selectionMode}
              selectedIds={selectedIds}
              onSelectTask={selectFromBoard}
              onBulkTaskMove={(taskIds, statusId) =>
                bulkUpdateMutation.mutate({ ids: taskIds, data: { statusId } })
              }
            />
          </ErrorBoundary>
        </div>
      )}

      {/* List View */}
      {view === 'list' && (
        <div className={overlay ? 'flex-1 min-h-0 overflow-y-auto -mx-6 px-6' : 'contents'}>
        {/* Tasks List */}
        {taskItems.length === 0 ? (
          <EmptyState
            icon={CheckSquare}
            title="No tasks yet"
            description="Add tasks to track your work"
            action={
              canCreateTask ? (
                <Button onClick={() => setShowCreateModal(true)}>
                  <Plus className="w-4 h-4" />
                  Add First Task
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="space-y-6">
            {/* Incomplete tasks with column headers */}
            {incompleteTasks.length > 0 && (
              <div>
                {/* Column Headers */}
                <div className="flex items-center gap-2 px-3 pb-2 mb-2 border-b border-grey-100">
                  {/* Spacer for drag handle + checkbox */}
                  <div className="w-[52px] flex-shrink-0" />
                  {/* Task title header */}
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium text-grey-400 uppercase tracking-wider">Task</span>
                  </div>
                  <div className="w-[100px] flex-shrink-0 text-center">
                    <span className="text-xs font-medium text-grey-400 uppercase tracking-wider">Assignee</span>
                  </div>
                  <div className="w-[110px] flex-shrink-0 text-center">
                    <span className="text-xs font-medium text-grey-400 uppercase tracking-wider">Status</span>
                  </div>
                  <div className="w-[90px] flex-shrink-0 text-center">
                    <span className="text-xs font-medium text-grey-400 uppercase tracking-wider">Priority</span>
                  </div>
                  {isAgencyMember && (
                    <div className="w-[100px] flex-shrink-0 text-center">
                      <span className="text-xs font-medium text-grey-400 uppercase tracking-wider">Start Date</span>
                    </div>
                  )}
                  <div className="w-[100px] flex-shrink-0 text-center">
                    <span className="text-xs font-medium text-grey-400 uppercase tracking-wider">Due Date</span>
                  </div>
                  {/* Spacer for actions */}
                  <div className="w-[28px] flex-shrink-0" />
                </div>

                {canReorderList ? (
                  <DndContext
                    sensors={listSensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleListDragEnd}
                  >
                    <SortableContext
                      items={incompleteTasks.map((t: any) => t.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2">
                        {incompleteTasks.map((t: any) => renderTask(t, { sortable: true }))}
                      </div>
                    </SortableContext>
                  </DndContext>
                ) : (
                  <div className="space-y-2">
                    {incompleteTasks.map((t: any) => renderTask(t))}
                  </div>
                )}
              </div>
            )}

            {/* Completed tasks - collapsible */}
            {(completedTasks.length > 0 || hiddenCompletedCount > 0) && (
              <div>
                <button
                  onClick={() => setShowCompleted(!showCompleted)}
                  className="flex items-center gap-2 text-sm font-medium text-grey-500 hover:text-secondary transition-colors mb-3"
                >
                  {showCompleted ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                  Completed ({completedTasks.length})
                  {hiddenCompletedCount > 0 && (
                    <span className="text-grey-400 font-normal">
                      · {hiddenCompletedCount} older
                    </span>
                  )}
                </button>
                {showCompleted && (
                  <div className="space-y-2">
                    {completedTasks.map((t: any) => renderTask(t))}
                    {hiddenCompletedCount > 0 && (
                      <p className="text-xs text-grey-400 text-center py-2">
                        {hiddenCompletedCount} older completed {hiddenCompletedCount === 1 ? 'task' : 'tasks'} hidden · use search to find them
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        </div>
      )}
      </Card>

      {/* Create Task Modal */}
      <TaskDetailModal
        create
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setCreateModalStatusId(undefined);
        }}
        projectId={projectId!}
        listId={listId!}
        defaultStatusId={createModalStatusId}
        onTaskCreated={(id) => setNewTaskId(id)}
      />
      {/* (Task editing happens inline inside TaskDetailModal — no separate edit modal.) */}

      {/* Edit List Modal */}
      {taskList && (
        <CreateTaskListModal
          isOpen={showEditListModal}
          projectId={projectId!}
          onClose={() => setShowEditListModal(false)}
          taskList={taskList}
        />
      )}

      {/* Delete list confirmation */}
      {showDeleteListConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-secondary/60 backdrop-blur-sm" onClick={() => setShowDeleteListConfirm(false)} />
          <div className="relative bg-white rounded-xl shadow-elevated p-6 max-w-sm w-full">
            <h3 className="text-heading-4 text-secondary mb-2">Delete List?</h3>
            <p className="text-grey-500 text-sm mb-6">
              This permanently deletes “{taskList?.name}” and all of its tasks. This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setShowDeleteListConfirm(false)}>Cancel</Button>
              <Button variant="danger" onClick={() => deleteListMutation.mutate()} isLoading={deleteListMutation.isPending}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk delete confirmation */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-secondary/60 backdrop-blur-sm" onClick={() => setShowBulkDeleteConfirm(false)} />
          <div className="relative bg-white rounded-xl shadow-elevated p-6 max-w-sm w-full">
            <h3 className="text-heading-4 text-secondary mb-2">
              Delete {selectedIds.size} {selectedIds.size === 1 ? 'task' : 'tasks'}?
            </h3>
            <p className="text-grey-500 text-sm mb-6">This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setShowBulkDeleteConfirm(false)}>Cancel</Button>
              <Button
                variant="danger"
                onClick={() => bulkDeleteMutation.mutate([...selectedIds])}
                isLoading={bulkDeleteMutation.isPending}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Task Modal */}
      {duplicatingTask && allTaskLists && (
        <DuplicateTaskModal
          isOpen={!!duplicatingTask}
          onClose={() => setDuplicatingTask(null)}
          task={duplicatingTask}
          projectId={projectId!}
          currentListId={listId!}
          taskLists={allTaskLists.map((l: any) => ({ id: l.id, name: l.name }))}
        />
      )}
    </div>
  );
}
